/**
 * Persistent storage.
 *
 * By default a browser treats site storage as evictable: it may clear our
 * IndexedDB when the device is short of space, or when the app has gone unused
 * for long enough. Asking for persistent storage tells the browser this is the
 * only copy of the user's records, not a cache it can rebuild.
 *
 * Whether the request is granted is entirely the browser's call. Chrome and
 * Safari decide from their own heuristics with no prompt (installing the app
 * makes a grant far more likely); Firefox asks the user. So we request once on
 * first load, remember that we asked, and never nag again on our own — but the
 * Settings screen offers a way to ask again, because a browser that said no
 * today may well say yes once the app is installed.
 *
 * The answer also sets how soon the backup reminder appears: without a
 * guarantee the data will survive, a week between backups is too long.
 */

import * as db from './db.js';
import { STORE_META } from './db.js';

/** Days between backups before the Today screen mentions it. */
export const BACKUP_DAYS_PERSISTED = 7;
export const BACKUP_DAYS_NOT_PERSISTED = 3;

const ASKED_KEY = 'storagePersistAsked';

/** The result is cached for the session; the browser will not change its mind mid-visit. */
let statePromise = null;

function isSupported() {
  return (
    typeof navigator !== 'undefined' &&
    navigator.storage &&
    typeof navigator.storage.persist === 'function' &&
    typeof navigator.storage.persisted === 'function'
  );
}

async function alreadyPersisted() {
  try {
    return await navigator.storage.persisted();
  } catch (err) {
    console.warn('Could not read the storage persistence state', err);
    return false;
  }
}

async function hasAsked() {
  try {
    const row = await db.get(STORE_META, ASKED_KEY);
    return Boolean(row && row.value);
  } catch {
    return false;
  }
}

async function markAsked() {
  try {
    await db.put(STORE_META, { key: ASKED_KEY, value: new Date().toISOString() });
  } catch (err) {
    console.warn('Could not record the storage persistence request', err);
  }
}

/**
 * Resolve the current state, requesting persistence if we have not asked before.
 * `force` asks again even if we have, for the button in Settings.
 */
async function resolve({ force = false } = {}) {
  if (!isSupported()) return { supported: false, persisted: false, asked: false };

  if (await alreadyPersisted()) return { supported: true, persisted: true, asked: true };

  if ((await hasAsked()) && !force) return { supported: true, persisted: false, asked: true };

  let persisted = false;
  try {
    persisted = await navigator.storage.persist();
  } catch (err) {
    console.warn('Could not request persistent storage', err);
  }
  await markAsked();
  return { supported: true, persisted, asked: true };
}

/**
 * The storage persistence state, requesting it on the first call of a session.
 * Returns { supported, persisted, asked }.
 */
export function getStoragePersistence() {
  if (!statePromise) statePromise = resolve();
  return statePromise;
}

/** Ask the browser again, from the Settings screen. */
export function requestStoragePersistence() {
  statePromise = resolve({ force: true });
  return statePromise;
}

/** Forget the cached answer, e.g. after the database has been deleted. */
export function invalidateStoragePersistence() {
  statePromise = null;
}

/** How many days may pass before the backup reminder appears. */
export function backupReminderDays(persisted) {
  return persisted ? BACKUP_DAYS_PERSISTED : BACKUP_DAYS_NOT_PERSISTED;
}
